import { createContext, useContext } from 'react';
import type { DashboardConfig, SectionConfig, WidgetConfig, WidgetDataSource, WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import type { WidgetRegistry, WidgetRegistryEntry } from '@trinity/chat-components/components/PageDashboard/registry/WidgetRegistry';

// ─── DashboardConfigContext ───────────────────────────────────────────────────

export interface DashboardConfigContextValue {
  /** The serializable config that drives the dashboard (source of truth) */
  config: DashboardConfig;
  /**
   * Mutate the config. Updater receives the current config and returns the
   * next one. Schema version is auto-upgraded on load.
   */
  setConfig: (updater: (prev: DashboardConfig) => DashboardConfig) => void;
}

export const DashboardConfigContext = createContext<DashboardConfigContextValue | null>(null);

export function useDashboardConfig(): DashboardConfigContextValue {
  const ctx = useContext(DashboardConfigContext);
  if (!ctx) throw new Error('useDashboardConfig must be used within DashboardBuilderProvider');
  return ctx;
}

// ─── DashboardDataContext ─────────────────────────────────────────────────────

export interface DashboardAgentOption {
  id: number;
  name: string;
}

export interface DashboardDataContextValue {
  /** Live data for each widget, keyed by widgetId */
  widgetData: Map<string, CanonicalWidgetData>;
  /**
   * True while the bulk data refresh (fetchAll) is in flight.
   * This is a coarse, global loading signal — does not indicate per-widget
   * refreshes (refreshWidget, fetchAgentPreview). Useful for showing overall
   * dashboard loading state during initial load or manual refresh-all actions.
   */
  isLoading: boolean;
  /** Re-fetch data for a single widget */
  refreshWidget: (widgetId: string) => void;
  /** Re-fetch data for all widgets */
  refreshAll: () => void;
  /**
   * Builder-only: proxy a request through the agent with the given ID and
   * inject the result as the live preview for the given widget.
   * Returns the fetched CanonicalWidgetData on success, or null on error.
   */
  fetchAgentPreview: (agentId: number, extensionPath: string, widgetId: string) => Promise<CanonicalWidgetData | null>;
  /**
   * Builder-only: directly write canonical data for a widget (e.g. from the
   * Config tab Apply button). Pass `null` to clear the widget's data.
   */
  updateWidgetData: (widgetId: string, data: CanonicalWidgetData | null) => void;
  /**
   * Builder-only: list of available HTTP agents to select from when configuring
   * an agent data source.  Populated by the host app (e.g. management-app).
   */
  agents: DashboardAgentOption[];
}

export const DashboardDataContext = createContext<DashboardDataContextValue | null>(null);

export function useDashboardData(widgetId: string): CanonicalWidgetData | null {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardData must be used within DashboardBuilderProvider');
  return ctx.widgetData.get(widgetId) ?? null;
}

export function useDashboardDataContext(): DashboardDataContextValue {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardDataContext must be used within DashboardBuilderProvider');
  return ctx;
}

/**
 * Convenience hook that combines data + isLoading for a specific widget.
 * Reads the context once, eliminating redundant reads when you need both values.
 * Returns { data, isLoading } for the given widgetId.
 */
export function useDashboardWidgetData(widgetId: string): { data: CanonicalWidgetData | null; isLoading: boolean } {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardWidgetData must be used within DashboardBuilderProvider');
  return {
    data: ctx.widgetData.get(widgetId) ?? null,
    isLoading: ctx.isLoading,
  };
}

// ─── WidgetRegistryContext ────────────────────────────────────────────────────

export interface WidgetRegistryContextValue {
  registry: WidgetRegistry;
  /** Convenience — returns all registered widget entries (for the palette). */
  getAvailableWidgets: () => WidgetRegistryEntry[];
}

export const WidgetRegistryContext = createContext<WidgetRegistryContextValue | null>(null);

export function useWidgetRegistry(): WidgetRegistryContextValue {
  const ctx = useContext(WidgetRegistryContext);
  if (!ctx) throw new Error('useWidgetRegistry must be used within DashboardBuilderProvider');
  return ctx;
}

// ─── DashboardBuilderContext (UI state) ───────────────────────────────────────

export type SidebarPanel = 'none' | 'chart-config' | 'create-widget';

export type PendingSlot = { blockId: string; row: number; slot: number };

export interface DashboardBuilderContextValue {
  /** UUID of the currently selected block (for sidebar config), or null if none */
  selectedBlockId: string | null;
  setSelectedBlockId: (id: string | null) => void;
  /** UUID of the currently selected widget (for sidebar config), or null if none */
  selectedWidgetId: string | null;
  setSelectedWidgetId: (id: string | null) => void;
  /** True when the config has unsaved changes */
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  /** Which config sidebar panel is currently open */
  sidebarPanel: SidebarPanel;
  setSidebarPanel: (panel: SidebarPanel) => void;
  /**
   * When false, the "Data Source" tab is hidden from the widget config sidebar.
   * Set to false in apps that use their own native data sources instead of
   * HTTP agents (e.g. atrium-backend / sentiment frontend).
   * Default: true.
   */
  showDataSource: boolean;
  /** The block/row/slot targeted for widget creation (used by 'create-widget' panel) */
  pendingSlot: PendingSlot | null;
  setPendingSlot: (slot: PendingSlot | null) => void;
  /** The widget type selected from the palette, awaiting sidebar configuration */
  pendingWidgetType: string | null;
  setPendingWidgetType: (type: string | null) => void;
}

export const DashboardBuilderContext = createContext<DashboardBuilderContextValue | null>(null);

export function useDashboardBuilder(): DashboardBuilderContextValue {
  const ctx = useContext(DashboardBuilderContext);
  if (!ctx) throw new Error('useDashboardBuilder must be used within DashboardBuilderProvider');
  return ctx;
}

// ─── DashboardBuilderEditingContext (mutations) ───────────────────────────────

export interface DashboardBuilderEditingContextValue {
  /** Add a new empty block (no widgets). Returns the new blockId. */
  addBlock: (opts?: { title?: string }) => string;
  /** Add a new block with a widget at slot 1. Returns the new widgetId. */
  addBlockWithWidget: (type: string, widgetConfig: Partial<WidgetSpecificConfig>, dataSource?: WidgetDataSource, rowSpanOverride?: number) => string;
  /** Remove a block and all its widgets by ID */
  removeBlock: (blockId: string) => void;
  /** Update a block's title */
  updateBlockTitle: (blockId: string, title: string) => void;
  /** Toggle whether a block's title is hidden in the rendered dashboard */
  toggleBlockHideTitle: (blockId: string) => void;
  /** Toggle whether a block is collapsible (accordion-style) in the rendered dashboard */
  toggleBlockCollapsible: (blockId: string) => void;
  /** Place a widget at a specific row and slot inside a block */
  assignWidgetToSlot: (blockId: string, row: number, slot: number, type: string, widgetConfig: Partial<WidgetSpecificConfig>, dataSource?: WidgetDataSource, rowSpanOverride?: number) => string;
  /** Remove the widget at a specific row and slot */
  removeWidgetAtSlot: (blockId: string, row: number, slot: number) => void;
  /** Move a widget from one row/slot/block to another */
  moveWidget: (fromBlockId: string, fromRow: number, fromSlot: number, toBlockId: string, toRow: number, toSlot: number) => void;
  /** Move a widget out of a section and into a block slot (cross-container) */
  moveWidgetFromSectionToBlock: (fromBlockId: string, fromSectionId: string, fromRow: number, fromSlot: number, toBlockId: string, toRow: number, toSlot: number) => void;
  /** Move a widget out of a block slot and into a section slot (cross-container) */
  moveWidgetFromBlockToSection: (fromBlockId: string, fromRow: number, fromSlot: number, toBlockId: string, toSectionId: string, toRow: number, toSlot: number) => void;
  /** Move a widget from one section to another (same or different block) */
  moveWidgetBetweenSections: (fromBlockId: string, fromSectionId: string, fromRow: number, fromSlot: number, toBlockId: string, toSectionId: string, toRow: number, toSlot: number) => void;
  /** Change how many columns a widget spans; displaced widgets are pushed to the next row */
  updateWidgetSpan: (blockId: string, row: number, slot: number, span: number) => void;
  /** Change how many rows a widget spans vertically; clamped to min 1 */
  updateWidgetRowSpan: (blockId: string, row: number, slot: number, rowSpan: number) => void;
  /**
   * Auto-size a widget's rowSpan based on its data using the registry's
   * `autoRowSpan` function. No-op if the widget type has no auto-sizing logic.
   */
  autoSizeWidget: (widgetId: string, data: CanonicalWidgetData) => void;
  /** Move a section from one row/slot to another within the same block */
  moveSection: (blockId: string, fromRow: number, fromSlot: number, toRow: number, toSlot: number) => void;
  /** Partially update a widget's config (merged with existing config) */
  updateWidgetConfig: (widgetId: string, partial: Partial<WidgetConfig['config']>) => void;
  /** Update a widget's data source (outer-level WidgetConfig field) */
  updateWidgetDataSource: (widgetId: string, dataSource: WidgetDataSource) => void;
  /** Reorder blocks to the given ordered list of IDs */
  reorderBlocks: (orderedIds: string[]) => void;
  // ── Section-scoped actions ────────────────────────────────────────────────
  /** Add a new empty section at a given (row, slot, span) inside a block */
  addSection: (blockId: string, row: number, slot: number, span: number, title?: string) => string;
  /** Remove a section from a block (and clean up its widgets' event bindings) */
  removeSection: (blockId: string, sectionId: string) => void;
  /** Update a section's title */
  updateSectionTitle: (blockId: string, sectionId: string, title: string) => void;
  /** Toggle whether a section's title is hidden in the rendered dashboard */
  toggleSectionHideTitle: (blockId: string, sectionId: string) => void;
  /** Toggle whether a section is collapsible (accordion-style) in the rendered dashboard */
  toggleSectionCollapsible: (blockId: string, sectionId: string) => void;
  /** Place a widget at a specific row and slot inside a section */
  assignWidgetToSection: (blockId: string, sectionId: string, row: number, slot: number, type: string, widgetConfig: Record<string, unknown>, dataSource?: WidgetDataSource) => string;
  /** Remove the widget at a specific row/slot inside a section */
  removeWidgetFromSection: (blockId: string, sectionId: string, row: number, slot: number) => void;
  /** Move a widget within a section (different row/slot) */
  moveWidgetInSection: (blockId: string, sectionId: string, fromRow: number, fromSlot: number, toRow: number, toSlot: number) => void;
  /** Change a widget's span inside a section */
  updateSectionWidgetSpan: (blockId: string, sectionId: string, row: number, slot: number, span: number) => void;
  /** Partially update a widget config located inside a section */
  updateWidgetConfigInSection: (sectionId: string, widgetId: string, partial: Partial<WidgetConfig['config']>) => void;
  /** Expose resolved section config for a given sectionId (utility for shells) */
  getSectionById: (blockId: string, sectionId: string) => SectionConfig | null;
  /** Change the column span of a section item in the parent block */
  updateSectionItemSpan: (blockId: string, sectionId: string, newSpan: number) => void;
  /**
   * Set a section's explicit minimum inner row count (absolute rows, not delta).
   * Called when the user drag-resizes the section's bottom handle.
   */
  updateSectionMinRows: (blockId: string, sectionId: string, rows: number) => void;
  /**
   * Set the block's explicit row count (absolute, not delta).
   * Called when the user drags the RGL resize handle to make the block taller/shorter.
   */
  setBlockRows: (blockId: string, rows: number) => void;
  /** Change a block's column width (bounded by the page's outerCols) */
  setBlockWidth: (blockId: string, w: number) => void;
  /**
   * Delete an entirely empty row from a block. Items in rows below the deleted
   * row are shifted up by one. minRows is decremented accordingly.
   */
  deleteEmptyRow: (blockId: string, row: number) => void;
  /**
   * Called when a drag ends \u2014 re-orders blocks by visual position AND persists
   * each block's column (x) in one atomic config update.
   */
  updateBlocksFromLayout: (orderedIds: string[], xMap: Record<string, number>) => void;
  /**
   * Append a new custom theme to DashboardConfig.customThemes.
   * Returns the newly assigned numeric ID so the caller can immediately select it.
   */
  createTheme: (name: string, colors: string[]) => number;
  /** Change the page's outer column count. Blocks wider than the new value are clamped. */
  setOuterCols: (cols: number) => void;
}

export const DashboardBuilderEditingContext = createContext<DashboardBuilderEditingContextValue | null>(null);

export function useDashboardBuilderEditing(): DashboardBuilderEditingContextValue {
  const ctx = useContext(DashboardBuilderEditingContext);
  if (!ctx) throw new Error('useDashboardBuilderEditing must be used within DashboardBuilderProvider');
  return ctx;
}

// ─── DashboardMutationContext (persistence) ───────────────────────────────────

export interface DashboardMutationContextValue {
  /** Persist current config as a new version */
  onSave: (description?: string) => Promise<void>;
  /** Discard unsaved changes */
  onCancel: () => void;
  /** Revert to a previous version by ID */
  onRevert: (versionId: string) => Promise<void>;
}

export const DashboardMutationContext = createContext<DashboardMutationContextValue | null>(null);

export function useDashboardMutation(): DashboardMutationContextValue {
  const ctx = useContext(DashboardMutationContext);
  if (!ctx) throw new Error('useDashboardMutation must be used within DashboardBuilderProvider');
  return ctx;
}
